/**
 * Sentry observability layer (core).
 *
 * Thin, env-gated wrapper around `@sentry/react` — the sole entry point for Sentry in this app.
 * Mirrors the Clarity layer's API surface (init, user identity, route/api/perf signals) so
 * consumers import from one surface (the barrel in index.ts) regardless of which sink is active.
 *
 * Design rules (identical posture to clarity.ts):
 *  - Sentry NEVER throws and NEVER blocks the UI. Every public fn is a no-op when disabled and
 *    swallows its own errors.
 *  - Disabled by default outside production unless VITE_ENABLE_SENTRY=true.
 *  - Never sends PII. The existing sanitizeTagValue / normalizeEndpoint / durationBucket helpers
 *    are reused so tag context on Sentry events is held to the same privacy bar as Clarity.
 *  - Session Replay is configured mask-all-text / block-all-media by default.
 *
 * See docs/observability/sentry-integration.md
 */

import * as Sentry from '@sentry/react';
import { sanitizeTagValue } from './clarity';
import { durationBucket, normalizeEndpoint } from './events';

// ── Sentry DSN (injected at build time via VITE_SENTRY_DSN) ─────────────────
const ENV = {
  dsn: import.meta.env.VITE_SENTRY_DSN as string | undefined,
  enableFlag: import.meta.env.VITE_ENABLE_SENTRY as string | undefined,
  environment: import.meta.env.VITE_APP_ENV || import.meta.env.MODE || 'development',
  appVersion: import.meta.env.VITE_APP_VERSION || 'unknown',
  releaseSha: import.meta.env.VITE_RELEASE_SHA || 'unknown',
  mode: import.meta.env.MODE,
  tracesSampleRate: Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? 0.2),
  replaysSessionSampleRate: Number(import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE ?? 0.1),
  replaysOnErrorSampleRate: Number(import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE ?? 1.0),
  enableLogs: import.meta.env.VITE_SENTRY_ENABLE_LOGS === 'true',
} as const;

const SENTRY_TEST_FLAG = '__bizcore_sentry_force__';

let cachedEnabled: boolean | undefined;

/** Whether Sentry should run in this environment. Cached after first evaluation. */
export function isSentryEnabled(): boolean {
  if (cachedEnabled !== undefined) return cachedEnabled;
  cachedEnabled = computeEnabled();
  return cachedEnabled;
}

function runtimeTestEnabled(): boolean {
  if (ENV.mode === 'production') return false;
  try {
    return typeof window !== 'undefined' && window.localStorage.getItem(SENTRY_TEST_FLAG) === '1';
  } catch {
    return false;
  }
}

function computeEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  if (!ENV.dsn) return false;
  if (ENV.enableFlag === 'false') return false;
  if (ENV.mode === 'production') return true;
  if (ENV.enableFlag === 'true') return true;
  return runtimeTestEnabled();
}

/** Test-only: reset the memoized enabled flag. */
export function __resetSentryEnabledCache(): void {
  cachedEnabled = undefined;
}

// ── Init ─────────────────────────────────────────────────────────────────────
let initialized = false;

/**
 * Initialize Sentry exactly once. Safe to call in any environment — no-op when disabled.
 * Call from the app entry point BEFORE React renders (as the first import in main.tsx).
 */
export function initSentry(): void {
  if (initialized || !isSentryEnabled()) return;
  initialized = true;

  try {
    Sentry.init({
      dsn: ENV.dsn,
      environment: ENV.environment,
      release: ENV.appVersion,

      // Performance monitoring
      tracesSampleRate: ENV.tracesSampleRate,
      tracePropagationTargets: ['localhost', /^https:\/\/[^/]+\.ingest\.us\.sentry\.io/],

      // Session Replay (client only)
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: true,
          blockAllMedia: true,
        }),
      ],
      replaysSessionSampleRate: ENV.replaysSessionSampleRate,
      replaysOnErrorSampleRate: ENV.replaysOnErrorSampleRate,

      // Structured logging
      enableLogs: ENV.enableLogs,

      // Data collection controls — respect the app's existing privacy posture
      dataCollection: {
        userInfo: false,        // identifyUser() controls this explicitly
        httpBodies: ['incomingRequest', 'incomingResponse'],
        queryParams: false,     // endpoints are normalized before reaching Sentry
      },
    });

    // Apply baseline tags from env
    Sentry.setTag('app_name', 'bizcore-web');
    Sentry.setTag('app_env', ENV.environment);
    Sentry.setTag('release_sha', ENV.releaseSha);

    installGlobalErrorListeners();
  } catch {
    /* Sentry must never break the app */
  }
}

let globalListenersInstalled = false;
function installGlobalErrorListeners(): void {
  if (globalListenersInstalled || typeof window === 'undefined') return;
  globalListenersInstalled = true;

  window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
    try {
      const reason = e.reason;
      if (reason instanceof Error) {
        Sentry.captureException(reason, {
          level: 'error',
          tags: { source: 'unhandledrejection' },
        });
      } else {
        Sentry.captureMessage(`Unhandled rejection: ${String(reason)}`, {
          level: 'error',
          tags: { source: 'unhandledrejection' },
        });
      }
    } catch {
      /* swallow */
    }
  });
}

// ── User identity ────────────────────────────────────────────────────────────
export interface SentryUserContext {
  /** Opaque, non-PII user id (guid). Never an email/phone/raw name. */
  id: string;
  /** Non-PII role label (e.g. 'admin', 'accountant'). */
  role?: string;
  /** Tenant/business type — already sanitised upstream. */
  tenantType?: string;
  /** Current plan name, if any. */
  plan?: string;
}

/**
 * Associate the current scope with a user. Only opaque non-PII fields are set.
 * Pass the data you already send to Clarity (from ClaritySession / useAuthStore).
 */
export function identifySentryUser(ctx: SentryUserContext | null): void {
  if (!isSentryEnabled()) return;

  try {
    if (ctx) {
      Sentry.setUser({
        id: sanitizeTagValue(ctx.id),
        role: ctx.role ? sanitizeTagValue(ctx.role) : undefined,
        tenant_type: ctx.tenantType ? sanitizeTagValue(ctx.tenantType) : undefined,
        plan: ctx.plan ? sanitizeTagValue(ctx.plan) : undefined,
      });
    } else {
      Sentry.setUser(null);
    }
  } catch {
    /* swallow */
  }
}

// ── Tags ─────────────────────────────────────────────────────────────────────
/**
 * Set Sentry event tags. Mirrors setClarityTags(). Keys are surfaced in the Sentry
 * dashboard as filterable dimensions.
 */
export function setSentryTags(tags: Record<string, string | number | boolean | null | undefined>): void {
  if (!isSentryEnabled()) return;
  try {
    for (const [key, value] of Object.entries(tags)) {
      if (value === null || value === undefined || value === '') continue;
      Sentry.setTag(key, sanitizeTagValue(String(value)));
    }
  } catch {
    /* swallow */
  }
}

// ── Route views ──────────────────────────────────────────────────────────────
/**
 * Set the current transaction name and associated tags so the route context is
 * attached to every subsequent event. Call on every route change.
 */
export function setSentryRouteContext(pathname: string, authState?: string): void {
  if (!isSentryEnabled()) return;
  try {
    const normalizedRoute = normalizeEndpoint(pathname);
    Sentry.setTag('route_id', normalizedRoute);
    Sentry.setTag('route_group', normalizedRoute.split('/')[1] || '/');
    if (authState) Sentry.setTag('auth_state', authState);
    Sentry.setTag('page_type', 'spa');
  } catch {
    /* swallow */
  }
}

// ── Error capture ────────────────────────────────────────────────────────────
export interface SentryErrorContext {
  /** Error object or message string. */
  error: Error | string;
  /** High-level area (e.g. 'api', 'ui', 'auth'). */
  area: string;
  /** Optional route where it happened. */
  route?: string;
  /** Optional severity level. */
  level?: 'fatal' | 'error' | 'warning' | 'info' | 'debug';
  /** Extra context data (non-PII only). */
  extra?: Record<string, unknown>;
  /** Optional tags to add to the event. */
  tags?: Record<string, string>;
}

/** Capture an exception through Sentry with structured context. */
export function captureSentryException(ctx: SentryErrorContext): void {
  if (!isSentryEnabled()) return;
  try {
    Sentry.withScope((scope) => {
      scope.setTag('error_area', ctx.area);
      if (ctx.route) scope.setTag('route_id', ctx.route);
      if (ctx.level) scope.setLevel(ctx.level);
      if (ctx.tags) {
        for (const [k, v] of Object.entries(ctx.tags)) {
          scope.setTag(k, v);
        }
      }
      if (ctx.extra) {
        scope.setContext('extra', ctx.extra);
      }

      if (typeof ctx.error === 'string') {
        Sentry.captureMessage(ctx.error, ctx.level ?? 'error');
      } else {
        Sentry.captureException(ctx.error);
      }
    });
  } catch {
    /* swallow */
  }
}

// ── API failures ─────────────────────────────────────────────────────────────
export interface SentryApiFailureContext {
  method: string;
  path: string;
  status?: number;
  isTimeout?: boolean;
  message?: string;
}

/** Capture an API failure as a Sentry error with structured breadcrumb context. */
export function captureSentryApiFailure(ctx: SentryApiFailureContext): void {
  if (!isSentryEnabled()) return;
  try {
    const endpoint = `${ctx.method.toUpperCase()} ${normalizeEndpoint(ctx.path)}`;
    const statusClass = ctx.isTimeout ? 'timeout' : !ctx.status ? 'network' : `${Math.floor(ctx.status / 100)}xx`;

    Sentry.addBreadcrumb({
      category: 'api',
      message: `${ctx.method} ${normalizeEndpoint(ctx.path)}`,
      level: ctx.status && ctx.status >= 500 ? 'error' : 'warning',
      data: {
        method: ctx.method,
        endpoint: normalizeEndpoint(ctx.path),
        status: ctx.status,
        statusClass,
      },
    });

    if (ctx.status && ctx.status >= 500) {
      const error = new Error(ctx.message || `API ${ctx.status} on ${endpoint}`);
      error.name = 'ApiError';
      captureSentryException({
        error,
        area: 'api',
        level: 'error',
        tags: {
          http_method: ctx.method,
          http_status: String(ctx.status),
          status_class: statusClass,
          endpoint,
        },
      });
    }
  } catch {
    /* swallow */
  }
}

// ── Performance ──────────────────────────────────────────────────────────────
export interface SentryPerformanceContext {
  kind: 'route' | 'api' | 'interaction';
  durationMs: number;
  label?: string;
}

/**
 * Report a performance signal as a Sentry measurement. Only captures signals that
 * exceed the slow threshold (matching the Clarity layer's behaviour).
 */
export function captureSentryPerformance(ctx: SentryPerformanceContext): void {
  if (!isSentryEnabled()) return;
  try {
    const bucket = durationBucket(ctx.durationMs);
    const isSlow = bucket !== 'lt_1s' && bucket !== '1_3s';

    Sentry.addBreadcrumb({
      category: 'performance',
      message: `${ctx.kind} ${bucket}`,
      level: isSlow ? 'warning' : 'info',
      data: {
        kind: ctx.kind,
        durationMs: ctx.durationMs,
        bucket,
        label: ctx.label,
      },
    });
  } catch {
    /* swallow */
  }
}

// ── Breadcrumbs ──────────────────────────────────────────────────────────────
/**
 * Add a structured breadcrumb to the current Sentry scope. Use for user interactions,
 * navigation events, or any significant app state change.
 */
export function addSentryBreadcrumb(category: string, message: string, data?: Record<string, unknown>): void {
  if (!isSentryEnabled()) return;
  try {
    Sentry.addBreadcrumb({
      category,
      message,
      level: 'info',
      data,
    });
  } catch {
    /* swallow */
  }
}

// Export Sentry's helper for use in the entry point (React 19 pattern)
export { reactErrorHandler } from '@sentry/react';
